/**
 * useRiskReadModel — Issue #400.
 *
 * Calls `/api/v1/risk/read-model/snapshots` series of endpoints (typed via
 * @jinzhuan/api-client). The page must never fake snapshot data; all rows
 * come from the authenticated backend.
 */
import { useCallback, useState } from 'react';
import {
  createApiClient,
  ApiError as ApiClientError,
  type ApiComponents,
} from '@/lib/adminApiClient';
import { getStoredAccessToken } from '@/lib/tokenStorage';

const apiClient = createApiClient({
  baseUrl: '',
  getAccessToken: () => getStoredAccessToken(),
});

export type RiskReadModelSnapshotSummaryDto =
  ApiComponents['schemas']['RiskReadModelSnapshotSummaryDto'];
export type RiskReadModelSnapshotDetailDto =
  ApiComponents['schemas']['RiskReadModelSnapshotDetailDto'];
export type RiskReadModelSnapshotListResponseDto =
  ApiComponents['schemas']['RiskReadModelSnapshotListResponseDto'];
export type RiskReadModelEventRowDto =
  ApiComponents['schemas']['RiskReadModelEventRowDto'];
export type RiskReadModelEventListResponseDto =
  ApiComponents['schemas']['RiskReadModelEventListResponseDto'];
export type RiskReadModelCustomerRollupDto =
  ApiComponents['schemas']['RiskReadModelCustomerRollupDto'];
export type RiskReadModelCustomerRollupListResponseDto =
  ApiComponents['schemas']['RiskReadModelCustomerRollupListResponseDto'];
export type RiskReadModelSourceStatusResponseDto =
  ApiComponents['schemas']['RiskReadModelSourceStatusResponseDto'];

export interface ApiError {
  message: string;
  code?: string;
}

function toApiError(err: unknown): ApiError {
  if (err instanceof ApiClientError) {
    if (err.status === 401)
      return { message: '会话已失效', code: 'UNAUTHENTICATED' };
    if (err.status === 403) return { message: '没有权限', code: 'FORBIDDEN' };
    if (err.status === 404) return { message: '快照不存在', code: 'NOT_FOUND' };
    if (err.status === 400) return { message: '请求参数错误', code: 'BAD_REQUEST' };
  }
  if (err instanceof Error) return { message: err.message };
  return { message: String(err) };
}

export type RiskReadModelSnapshotStatus =
  | 'draft'
  | 'generated'
  | 'reviewed'
  | 'archived';

export type RiskReadModelSourceModule =
  | 'follow_up_outcome_incident'
  | 'complaint_work_order'
  | 'compliance_recheck_task'
  | 'follow_up_report_retraction';

export type RiskReadModelLevel = 'low' | 'medium' | 'high' | 'critical';
export type RiskReadModelStatus =
  | 'open'
  | 'in_progress'
  | 'waiting'
  | 'resolved'
  | 'cancelled';
export type RiskReadModelSlaStatus =
  | 'not_applicable'
  | 'on_track'
  | 'due_soon'
  | 'overdue';

export interface SnapshotListFilter {
  readonly status?: RiskReadModelSnapshotStatus;
  readonly page?: number;
  readonly limit?: number;
}

export function useRiskReadModelSnapshots() {
  const [data, setData] =
    useState<RiskReadModelSnapshotListResponseDto | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const fetchList = useCallback(async (filter: SnapshotListFilter = {}) => {
    setLoading(true);
    setError(null);
    try {
      const result = await apiClient.GET(
        '/api/v1/risk/read-model/snapshots',
        {
          params: {
            query: {
              status: filter.status,
              page: filter.page,
              limit: filter.limit,
            },
          },
        },
      );
      setData(result.data ?? null);
    } catch (err) {
      setError(toApiError(err));
    } finally {
      setLoading(false);
    }
  }, []);

  return { data, loading, error, fetchList };
}

export function useRiskReadModelSnapshotDetail() {
  const [data, setData] =
    useState<RiskReadModelSnapshotDetailDto | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const fetchDetail = useCallback(async (id: string) => {
    setLoading(true);
    setError(null);
    try {
      const result = await apiClient.GET(
        '/api/v1/risk/read-model/snapshots/{id}',
        { params: { path: { id } } },
      );
      setData(result.data ?? null);
    } catch (err) {
      setError(toApiError(err));
    } finally {
      setLoading(false);
    }
  }, []);

  return { data, loading, error, fetchDetail };
}

export function useGenerateRiskReadModelSnapshot() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const generate = useCallback(
    async (
      notes: string,
    ): Promise<RiskReadModelSnapshotSummaryDto | null> => {
      setLoading(true);
      setError(null);
      try {
        const result = await apiClient.POST(
          '/api/v1/risk/read-model/snapshots/generate',
          { body: { notes } },
        );
        return result.data ?? null;
      } catch (err) {
        setError(toApiError(err));
        return null;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return { loading, error, generate };
}

export function useReviewRiskReadModelSnapshot() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const review = useCallback(
    async (
      id: string,
      expectedVersion: number,
      notes?: string,
    ): Promise<RiskReadModelSnapshotSummaryDto | null> => {
      setLoading(true);
      setError(null);
      try {
        const result = await apiClient.POST(
          '/api/v1/risk/read-model/snapshots/{id}/review',
          {
            params: { path: { id } },
            body: { expectedVersion, notes },
          },
        );
        return result.data ?? null;
      } catch (err) {
        setError(toApiError(err));
        return null;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return { loading, error, review };
}

export function useArchiveRiskReadModelSnapshot() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const archive = useCallback(
    async (
      id: string,
      expectedVersion: number,
    ): Promise<RiskReadModelSnapshotSummaryDto | null> => {
      setLoading(true);
      setError(null);
      try {
        const result = await apiClient.POST(
          '/api/v1/risk/read-model/snapshots/{id}/archive',
          {
            params: { path: { id } },
            body: { expectedVersion },
          },
        );
        return result.data ?? null;
      } catch (err) {
        setError(toApiError(err));
        return null;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return { loading, error, archive };
}

export function useRiskReadModelEvents() {
  const [data, setData] =
    useState<RiskReadModelEventListResponseDto | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const fetchRows = useCallback(
    async (
      snapshotId: string,
      filter: {
        sourceModule?: RiskReadModelSourceModule;
        riskLevel?: RiskReadModelLevel;
        status?: RiskReadModelStatus;
        slaStatus?: RiskReadModelSlaStatus;
        customerCode?: string;
        responsibleUserCode?: string;
      },
    ) => {
      setLoading(true);
      setError(null);
      try {
        const result = await apiClient.GET(
          '/api/v1/risk/read-model/snapshots/{id}/events',
          {
            params: {
              path: { id: snapshotId },
              query: {
                sourceModule: filter.sourceModule,
                riskLevel: filter.riskLevel,
                status: filter.status,
                slaStatus: filter.slaStatus,
                customerCode: filter.customerCode,
                responsibleUserCode: filter.responsibleUserCode,
              },
            },
          },
        );
        setData(result.data ?? null);
      } catch (err) {
        setError(toApiError(err));
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return { data, loading, error, fetchRows };
}

export function useRiskReadModelCustomerRollups() {
  const [data, setData] =
    useState<RiskReadModelCustomerRollupListResponseDto | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const fetchRows = useCallback(
    async (
      snapshotId: string,
      filter: {
        customerCode?: string;
        highestRiskLevel?: RiskReadModelLevel;
        worstSlaStatus?: RiskReadModelSlaStatus;
      },
    ) => {
      setLoading(true);
      setError(null);
      try {
        const result = await apiClient.GET(
          '/api/v1/risk/read-model/snapshots/{id}/customer-rollups',
          {
            params: {
              path: { id: snapshotId },
              query: {
                customerCode: filter.customerCode,
                highestRiskLevel: filter.highestRiskLevel,
                worstSlaStatus: filter.worstSlaStatus,
              },
            },
          },
        );
        setData(result.data ?? null);
      } catch (err) {
        setError(toApiError(err));
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return { data, loading, error, fetchRows };
}

export function useRiskReadModelSourceStatus() {
  const [data, setData] =
    useState<RiskReadModelSourceStatusResponseDto | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const fetchStatus = useCallback(async (id: string) => {
    setLoading(true);
    setError(null);
    try {
      const result = await apiClient.GET(
        '/api/v1/risk/read-model/snapshots/{id}/source-status',
        { params: { path: { id } } },
      );
      setData(result.data ?? null);
    } catch (err) {
      setError(toApiError(err));
    } finally {
      setLoading(false);
    }
  }, []);

  return { data, loading, error, fetchStatus };
}

// Export uses authenticated fetch (typed apiClient.GET expects JSON; the
// export endpoint returns binary/CSV). Mirrors useLabTraceability download
// behaviour, including content-type guard.
export async function downloadRiskReadModelExport(args: {
  snapshotId: string;
  reason: string;
  format?: 'csv' | 'xlsx';
}): Promise<void> {
  const token = getStoredAccessToken();
  if (!token) {
    throw new Error('未登录或会话已失效，请重新登录后再导出');
  }
  const params = new URLSearchParams();
  params.set('reason', args.reason);
  params.set('format', args.format ?? 'csv');
  const response = await fetch(
    `/api/v1/risk/read-model/snapshots/${encodeURIComponent(args.snapshotId)}/export?${params.toString()}`,
    {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
        Accept:
          (args.format ?? 'csv') === 'xlsx'
            ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
            : 'text/csv',
      },
      credentials: 'include',
    },
  );
  if (!response.ok) {
    throw new Error(`导出失败：${response.status}`);
  }
  const rawContentType = response.headers.get('content-type') ?? '';
  const contentType = rawContentType.toLowerCase();
  const expectedFormat = args.format ?? 'csv';
  const expectedContentType =
    expectedFormat === 'xlsx'
      ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      : 'text/csv';
  const matchesCsv =
    expectedFormat === 'csv' &&
    (contentType.startsWith('text/csv') ||
      contentType.startsWith('application/csv'));
  const matchesXlsx =
    expectedFormat === 'xlsx' &&
    contentType.startsWith(
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    );
  if (!matchesCsv && !matchesXlsx) {
    throw new Error(
      `导出 content-type 校验失败：HTTP ${response.status}, content-type=${rawContentType || '(empty)'}, expected ${expectedContentType}`,
    );
  }
  const blob = await response.blob();
  const filename =
    extractFilename(response.headers.get('content-disposition')) ??
    `risk-read-model-${args.snapshotId}.${args.format ?? 'csv'}`;
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

function extractFilename(headerValue: string | null): string | null {
  if (!headerValue) return null;
  const match = /filename="?([^";]+)"?/.exec(headerValue);
  return match && match[1] ? match[1] : null;
}
